import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MapPin, Plus, ArrowRight, Trash2, X } from "lucide-react";
import { fetchAreas, addArea, addMine, deleteArea, deleteMine } from "@/api";

export default function MinePicker({ onSelect }) {
  const [areas, setAreas] = useState([]);
  const [areaId, setAreaId] = useState("");
  const [mineId, setMineId] = useState("");
  const [newArea, setNewArea] = useState("");
  const [newMine, setNewMine] = useState("");

  const load = async () => {
    try {
      setAreas(await fetchAreas());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const area = areas.find((a) => a.id === areaId);
  const mines = area?.mines || [];

  const handleAddArea = async () => {
    if (!newArea.trim()) return;
    try {
      const a = await addArea(newArea);
      setNewArea("");
      await load();
      setAreaId(a.id);
      setMineId("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add area");
    }
  };

  const handleAddMine = async () => {
    if (!newMine.trim() || !areaId) return;
    try {
      const m = await addMine(areaId, newMine);
      setNewMine("");
      await load();
      setMineId(m.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add mine");
    }
  };

  const cont = () => {
    const mine = mines.find((m) => m.id === mineId);
    if (!area || !mine) return;
    onSelect({ area_id: area.id, area_name: area.name, mine_id: mine.id, mine_name: mine.name });
  };

  const handleDeleteArea = async () => {
    if (!area || !window.confirm(`Delete area ${area.name} with ALL its mines, fleet, shifts and trips?`)) return;
    try {
      await deleteArea(area.id);
      toast.success("Area deleted");
      setAreaId("");
      setMineId("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete area");
    }
  };

  const handleDeleteMine = async (m) => {
    if (!window.confirm(`Delete mine ${m.name} with ALL its fleet, shifts and trips?`)) return;
    try {
      await deleteMine(areaId, m.id);
      toast.success("Mine deleted");
      if (mineId === m.id) setMineId("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete mine");
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-start justify-center pt-16 px-4" data-testid="mine-picker">
      <div className="w-full max-w-md border-2 border-black bg-white hard-shadow p-6">
        <h1 className="font-head text-2xl font-black tracking-tight uppercase flex items-center gap-2">
          <MapPin size={24} strokeWidth={3} /> Select Area & Mine
        </h1>
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mt-1 mb-6">
          Data is kept separate for each mine
        </p>

        <label className="block text-xs font-bold uppercase tracking-widest mb-1">Area</label>
        <select
          data-testid="area-select"
          value={areaId}
          onChange={(e) => {
            setAreaId(e.target.value);
            setMineId("");
          }}
          className="w-full h-14 border-2 border-black px-3 text-lg font-bold bg-white mb-3"
        >
          <option value="">-- Select Area --</option>
          {areas.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        {(
          <div className="flex gap-2 mb-5">
            <input
              data-testid="add-area-input"
              value={newArea}
              onChange={(e) => setNewArea(e.target.value)}
              placeholder="New area name"
              className="flex-1 h-12 border-2 border-black px-3 font-bold uppercase focus:outline-none"
            />
            <button
              data-testid="add-area-btn"
              onClick={handleAddArea}
              className="h-12 w-12 border-2 border-black bg-[#FFB800] hard-shadow-sm pressable flex items-center justify-center"
            >
              <Plus size={20} strokeWidth={3} />
            </button>
          </div>
        )}

        <label className="block text-xs font-bold uppercase tracking-widest mb-1">Mine</label>
        <select
          data-testid="mine-select"
          value={mineId}
          disabled={!areaId}
          onChange={(e) => setMineId(e.target.value)}
          className="w-full h-14 border-2 border-black px-3 text-lg font-bold bg-white mb-3 disabled:opacity-40"
        >
          <option value="">-- Select Mine --</option>
          {mines.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>

        {areaId && (
          <div className="flex gap-2 mb-5">
            <input
              data-testid="add-mine-input"
              value={newMine}
              onChange={(e) => setNewMine(e.target.value)}
              placeholder="New mine name"
              className="flex-1 h-12 border-2 border-black px-3 font-bold uppercase focus:outline-none"
            />
            <button
              data-testid="add-mine-btn"
              onClick={handleAddMine}
              className="h-12 w-12 border-2 border-black bg-[#FFB800] hard-shadow-sm pressable flex items-center justify-center"
            >
              <Plus size={20} strokeWidth={3} />
            </button>
          </div>
        )}

        {area && (
          <div className="border-2 border-black bg-zinc-100 p-3 mb-5" data-testid="area-mine-manage">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-widest">Mines in {area.name}</span>
              <button
                data-testid="delete-area-btn"
                onClick={handleDeleteArea}
                className="flex items-center gap-1 border-2 border-black bg-[#FF3B30] text-white px-2 py-1 text-xs font-bold uppercase pressable hard-shadow-sm"
              >
                <Trash2 size={12} strokeWidth={3} /> Delete Area
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {mines.map((m) => (
                <span
                  key={m.id}
                  data-testid={`mine-chip-${m.name}`}
                  className="inline-flex items-center gap-1 border-2 border-black bg-white px-2 py-1 font-bold text-sm"
                >
                  {m.name}
                  <button
                    data-testid={`delete-mine-${m.name}`}
                    onClick={() => handleDeleteMine(m)}
                    className="hover:text-[#FF3B30]"
                  >
                    <X size={14} strokeWidth={3} />
                  </button>
                </span>
              ))}
              {mines.length === 0 && (
                <span className="text-xs text-zinc-400 font-medium">No mines in this area</span>
              )}
            </div>
          </div>
        )}

        <button
          data-testid="mine-continue-btn"
          onClick={cont}
          disabled={!mineId}
          className="w-full min-h-[64px] border-2 border-black bg-[#FFB800] hard-shadow pressable font-head text-xl font-black uppercase flex items-center justify-center gap-2 disabled:opacity-50"
        >
          Continue <ArrowRight size={22} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}
